import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Phone, Mail, Clock, Send, Heart } from "lucide-react";
import { Overline, MaskLine, EASE } from "@/components/Motion";
import { CONTACT, HOURS } from "@/data/content";

const API = process.env.REACT_APP_BACKEND_URL;

const TOPICS = ["General enquiry", "Reservations", "Catering & Events", "Bakery orders", "Feedback"];

const inputCls = (hasError) =>
  `w-full rounded-2xl border bg-white/70 px-5 py-3.5 text-[15px] font-light outline-none transition-colors duration-300 placeholder:text-faded/60 focus:border-wine ${
    hasError ? "border-red-500" : "border-sand"
  }`;

const Err = ({ id, msg }) =>
  msg ? (
    <p data-testid={`contact-error-${id}`} className="mt-1.5 text-xs text-red-600 tracking-wide">
      {msg}
    </p>
  ) : null;

const EMPTY = { name: "", email: "", phone: "", topic: "", message: "" };

export default function ContactPage() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((er) => ({ ...er, [key]: undefined }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = {};
    if (form.name.trim().length < 2) er.name = "Please tell us your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) er.email = "Please enter a valid email address.";
    if (form.phone && form.phone.replace(/\D/g, "").length < 10) er.phone = "Please enter a valid 10-digit phone number.";
    if (!form.topic) er.topic = "Please pick a topic.";
    if (form.message.trim().length < 10) er.message = "Please tell us a little more (at least 10 characters).";
    if (Object.keys(er).length) {
      setErrors(er);
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await axios.post(`${API}/api/contact`, form);
      setSent({ ...form });
    } catch (err) {
      setSubmitError(`Something went wrong — please try again, or write to us directly at ${CONTACT.email}.`);
    } finally {
      setSubmitting(false);
    }
  };

  const INFO = [
    { icon: MapPin, label: "Find Us", lines: [CONTACT.address], testId: "contact-info-address" },
    { icon: Phone, label: "Call Us", lines: [CONTACT.phone], testId: "contact-info-phone" },
    { icon: Mail, label: "Write to Us", lines: [CONTACT.email], testId: "contact-info-email" },
    { icon: Clock, label: "Hours", lines: HOURS.map((h) => `${h.days} — ${h.time}`), testId: "contact-info-hours" },
  ];

  return (
    <main className="pt-[72px]" data-testid="contact-page">
      <section className="bg-wine text-cream py-20 sm:py-28">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <Overline className="text-brass">Contact</Overline>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-5 max-w-4xl" data-testid="contact-page-title">
            <MaskLine>Come Say</MaskLine>
            <MaskLine delay={0.15}>
              <span className="italic text-brass">Ciao.</span>
            </MaskLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.4, ease: EASE }}
            className="mt-6 max-w-xl text-cream/80 text-base sm:text-lg font-light leading-relaxed"
          >
            Questions, catering enquiries or just a craving — we'd love to hear from you.
          </motion.p>
        </div>
      </section>

      <section className="py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {INFO.map((item, i) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 + i * 0.1, ease: EASE }}
                className="rounded-[1.75rem] border border-sand bg-shell p-7"
                data-testid={item.testId}
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-wine/25 text-wine">
                  <item.icon size={19} strokeWidth={1.4} />
                </span>
                <p className="mt-5 text-[11px] tracking-[0.3em] uppercase text-brass">{item.label}</p>
                {item.lines.map((line) => (
                  <p key={line} className="mt-1.5 text-sm font-light leading-relaxed">{line}</p>
                ))}
              </motion.div>
            ))}
          </div>

          <div className="mt-14 grid gap-10 lg:grid-cols-2 items-stretch">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3, ease: EASE }}
              className="overflow-hidden rounded-[2rem] border border-sand min-h-[420px]"
              data-testid="contact-map-wrapper"
            >
              <iframe
                title="Forco Amore — DLF Corporate Greens, Sector 74A, Gurugram"
                src="https://www.google.com/maps?q=DLF+Corporate+Greens,+Sector+74A,+Gurugram+122004&output=embed"
                className="h-full w-full min-h-[420px] border-0"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="contact-map-iframe"
              />
            </motion.div>

            <AnimatePresence mode="wait">
              {!sent ? (
                <motion.form
                  key="form"
                  noValidate
                  onSubmit={onSubmit}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.7, delay: 0.35, ease: EASE }}
                  className="rounded-[2rem] border border-sand bg-shell p-8 sm:p-10"
                  data-testid="contact-form"
                >
                  <h2 className="font-serif font-light text-3xl sm:text-4xl tracking-tight">
                    Quick <span className="italic text-wine">Enquiry</span>
                  </h2>

                  <div className="mt-8 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Your Name *</label>
                      <input type="text" value={form.name} onChange={set("name")} placeholder="e.g. Aarav Sharma" className={inputCls(errors.name)} data-testid="contact-name-input" />
                      <Err id="name" msg={errors.name} />
                    </div>
                    <div>
                      <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Email *</label>
                      <input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls(errors.email)} data-testid="contact-email-input" />
                      <Err id="email" msg={errors.email} />
                    </div>
                    <div>
                      <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Phone (optional)</label>
                      <input type="tel" value={form.phone} onChange={set("phone")} placeholder="+91 98XXXXXXXX" className={inputCls(errors.phone)} data-testid="contact-phone-input" />
                      <Err id="phone" msg={errors.phone} />
                    </div>
                    <div>
                      <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Topic *</label>
                      <select value={form.topic} onChange={set("topic")} className={inputCls(errors.topic)} data-testid="contact-topic-select">
                        <option value="">Choose a topic</option>
                        {TOPICS.map((t) => (
                          <option key={t} value={t}>{t}</option>
                        ))}
                      </select>
                      <Err id="topic" msg={errors.topic} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-2 block text-[11px] tracking-[0.25em] uppercase text-faded">Message *</label>
                      <textarea rows={4} value={form.message} onChange={set("message")} placeholder="Tell us how we can help…" className={`${inputCls(errors.message)} resize-none`} data-testid="contact-message-input" />
                      <Err id="message" msg={errors.message} />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit-btn"
                    className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-full bg-wine px-12 py-5 text-sm tracking-[0.16em] uppercase text-cream hover:bg-winedark hover:scale-[1.02] active:scale-95 transition-all duration-300 shadow-[0_20px_50px_-15px_rgba(114,47,55,0.5)] disabled:opacity-60 disabled:pointer-events-none"
                  >
                    <Send size={16} strokeWidth={1.5} />
                    {submitting ? "Sending…" : "Send Enquiry"}
                  </button>
                  {submitError && (
                    <p className="mt-4 text-center text-sm text-red-600" data-testid="contact-submit-error">{submitError}</p>
                  )}
                  <p className="mt-4 text-center text-xs text-faded tracking-wide">
                    Prefer email? Write to us at {CONTACT.email}
                  </p>
                </motion.form>
              ) : (
                <motion.div
                  key="thanks"
                  initial={{ opacity: 0, scale: 0.96, y: 30 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 0.8, ease: EASE }}
                  className="flex flex-col items-center justify-center rounded-[2rem] border border-sand bg-shell p-8 sm:p-12 text-center"
                  data-testid="contact-thankyou"
                >
                  <span className="flex h-20 w-20 items-center justify-center rounded-full bg-wine text-cream">
                    <Heart size={30} strokeWidth={1.4} className="fill-current" />
                  </span>
                  <h2 className="mt-8 font-serif font-light text-3xl sm:text-4xl tracking-tight" data-testid="contact-thankyou-title">
                    Grazie, <span className="italic text-wine">{sent.name.split(" ")[0]}!</span>
                  </h2>
                  <p className="mt-4 text-faded text-base font-light leading-relaxed max-w-md">
                    Your message about "{sent.topic}" is with us. We'll reply to {sent.email} as soon
                    as the espresso machine takes a breath.
                  </p>
                  <button
                    onClick={() => {
                      setForm(EMPTY);
                      setSent(null);
                    }}
                    data-testid="contact-new-enquiry-btn"
                    className="mt-10 rounded-full border border-wine px-9 py-4 text-sm tracking-[0.14em] uppercase text-wine hover:bg-wine hover:text-cream transition-colors duration-300"
                  >
                    Send Another Message
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </main>
  );
}
